import { useCallback, useEffect, useRef, useState } from "react";
import { SearchX, Send } from "lucide-react";
import {
  Button,
  EmptyState,
  FilterChips,
  ListCard,
  SearchField,
  SkeletonList,
  SortSelect,
  StatusBadge,
  Timestamp,
  useSubmittingMutation,
} from "@/design-system/components/ui";
import {
  PREQUAL_INVITE_FILTER_STATUSES,
  PREQUAL_INVITE_STATUS_TONE,
  isOutstandingInviteStatus,
  isTerminalInviteStatus,
  terminalInviteReason,
} from "@/design-system/lib/prequalInviteStatus";
import {
  prequalSiteApi,
  type PrequalInvite,
  type PrequalInvitePage,
  type PrequalInviteStatus,
} from "../../api/prequalSite";
import {
  errorMessage,
  PREQUAL_PAGE_SIZE,
  PREQUAL_SORT_OPTIONS,
  type InvitesPanelProps,
  type PrequalSort,
} from "./panelProps";
import { PrequalPager } from "./PrequalPager";

// Partner Brand Ownership — the "Invitations" list.
//
// The tracked outreach half of the tab: who was invited to prequalify from the
// caller's site, where each one has got to, and the two actions that move one
// along. List-ONLY — the send form that used to sit on top of this list is now
// the shell's wizard, so nothing here composes an invite.
//
// It owns its own page / search / sort / status state. The shell owns exactly
// one thing this reads: the Mine/Everyone scope, which is shared across all
// three lists so they can never be showing different populations.
//
// Portal-local by the isolation rule: design-system kit + portal-relative
// imports only, and no React Query (plain useState loads, mirroring the other
// portal tabs). Writes go through useSubmittingMutation with the list loader as
// `refresh` — a response-DTO setState alone would leave the list stale.

/** The "no status filter" chip id. Sends no `status` param at all, which is
 *  what the server reads as "every status". */
/** Keystrokes are cheap; a paged server query over an unbounded row count is
 *  not. Matches the portal's established search debounce (PreleadsTab), and
 *  named here because the sibling panels name it — it was the one inline `300`
 *  of the three, which is how a shared cadence starts to drift. */
const SEARCH_DEBOUNCE_MS = 300;

const ALL_STATUSES = "all";

/**
 * Display labels for the status chips.
 *
 * Only the one entry, because the rest of the union already reads as English
 * and inventing synonyms for them would put a second vocabulary beside the
 * badge text on the rows below — the chip and the pill must say the same word
 * or the filter looks like it did nothing. `ClosedBySibling` is the exception:
 * raw PascalCase is not a label, and "sibling" is our word for it, not a
 * partner's.
 *
 * The VALUE sent to the server stays the exact enum name in every case; only
 * the rendered text is overridden.
 */
const STATUS_CHIP_LABEL: Partial<Record<PrequalInviteStatus, string>> = {
  ClosedBySibling: "Closed by another invite",
};

function statusChipLabel(status: PrequalInviteStatus): string {
  return STATUS_CHIP_LABEL[status] ?? status;
}

/**
 * The status chips, from the SHARED filter list — never a hand-written array.
 *
 * `Unavailable` is absent and must stay absent even though the union has eight
 * members: it is not a persisted status, it is derived per request from the
 * SENDING BROKER's status through a join no list read performs, so no row this
 * list can produce could ever carry it. A chip for it would be a filter that
 * always returns nothing. The shared constant already excludes it — this is
 * why it is imported rather than re-typed here.
 */
const STATUS_CHIPS = [
  { id: ALL_STATUSES, label: "All" },
  ...PREQUAL_INVITE_FILTER_STATUSES.map((status) => ({
    id: status,
    label: statusChipLabel(status),
  })),
];

export function InvitesPanel({ mineOnly }: InvitesPanelProps) {
  const [invites, setInvites] = useState<PrequalInvitePage | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Null is "All" — held as null rather than a sentinel string so it is the
  // absence of a param at the call site too, not a value the server has to
  // recognise.
  const [status, setStatus] = useState<PrequalInviteStatus | null>(null);
  const [rawSearch, setRawSearch] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [sort, setSort] = useState<PrequalSort>("newest");
  const [page, setPage] = useState(1);

  // Last-request-wins: changing any control leaves two loads in flight, and
  // without this the slower one can repaint the filter the user just left.
  const requestSeq = useRef(0);

  // The scope is the THIRD control that invalidates the current page, and the
  // only one that arrives as a PROP rather than through a handler in this file
  // — status and sort both reset it a few lines below, which is how the scope
  // came to be the one that did not. Reconciled during render, the
  // React-sanctioned adjust-state-on-prop-change shape: an effect would work
  // too, but it would fire a doomed page-2 request first and flash an empty
  // list on the way past. DraftsPanel already did this.
  const [prevScope, setPrevScope] = useState(mineOnly);
  if (prevScope !== mineOnly) {
    setPrevScope(mineOnly);
    setPage(1);
  }

  // Debounced text search, 300ms — the established portal cadence. Resetting
  // the page keeps a narrowing search on page 1 rather than stranding it on a
  // page number the new result set no longer has.
  useEffect(() => {
    // Nothing to debounce — including on mount, where this effect otherwise
    // scheduled an unconditional setPage(1) that landed ~300ms in and silently
    // reverted a Next pressed inside that window.
    const trimmed = rawSearch.trim();
    if (trimmed === debouncedQ) return;
    const handle = window.setTimeout(() => {
      setDebouncedQ(trimmed);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [rawSearch, debouncedQ]);

  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    const result = await prequalSiteApi.invites({
      status: status ?? undefined,
      // Blank is dropped by the client's query builder, so an empty box is
      // indistinguishable from an unsent param.
      q: debouncedQ || undefined,
      sort,
      mineOnly,
      page,
      pageSize: PREQUAL_PAGE_SIZE,
    });
    if (seq === requestSeq.current) {
      setInvites(result);
      // Clearing on SUCCESS is the point. Without it one transient failure
      // pinned the red line above a list that was refreshing correctly
      // underneath it, for the rest of the session — the in-progress list
      // beside this one always cleared its own error and the two were
      // silently asymmetric.
      setListError(null);
    }
  }, [status, debouncedQ, sort, mineOnly, page]);

  useEffect(() => {
    let cancelled = false;
    void load().catch((err: unknown) => {
      if (!cancelled) setListError(errorMessage(err, "Couldn't load your invitations."));
    });
    return () => {
      cancelled = true;
    };
  }, [load]);

  const resend = useSubmittingMutation((id: string) => prequalSiteApi.resendInvite(id), {
    refresh: load,
    onSuccess: () => setActionError(null),
    onError: (err) => setActionError(errorMessage(err, "Couldn't resend that invitation.")),
  });

  const revoke = useSubmittingMutation((id: string) => prequalSiteApi.revokeInvite(id), {
    refresh: load,
    onSuccess: () => setActionError(null),
    onError: (err) => setActionError(errorMessage(err, "Couldn't revoke that invitation.")),
  });

  async function copyLink(invite: PrequalInvite) {
    try {
      await navigator.clipboard.writeText(invite.inviteUrl);
      setActionError(null);
      setCopiedId(invite.id);
    } catch {
      setCopiedId(null);
      // The link itself, not a shrug: the partner wanted this string, and a
      // clipboard the browser refused is no reason to withhold it.
      setActionError(`Couldn't copy the link. It's ${invite.inviteUrl}`);
    }
  }

  function handleStatus(id: string) {
    setStatus(id === ALL_STATUSES ? null : (id as PrequalInviteStatus));
    setPage(1);
  }

  function handleSort(value: string) {
    setSort(value as PrequalSort);
    setPage(1);
  }

  const rows = invites?.data ?? [];
  // Whether the EMPTY state should read "nothing matches" rather than "nothing
  // yet". The shared Mine/Everyone scope is deliberately NOT counted: it is the
  // shell's control, it is always set to something, and telling a partner their
  // filters are hiding rows when they have touched none of the ones on this
  // panel would send them looking in the wrong place.
  const filtered = status !== null || debouncedQ.length > 0;

  return (
    <div className="flex flex-col gap-space-3" data-testid="prequal-invites-panel">
      <div className="flex flex-col gap-space-3">
        <FilterChips
          label="Invitation status"
          activeId={status ?? ALL_STATUSES}
          onChange={handleStatus}
          chips={STATUS_CHIPS}
        />
        <div className="flex flex-wrap items-center gap-space-2">
          {/* No `label` — it defaults to "Search", and that default is
              load-bearing: a label naming the email field would collide with
              the send wizard's own Email input for anything looking a control
              up by its accessible name. */}
          <SearchField
            value={rawSearch}
            onChange={setRawSearch}
            placeholder="Search by name or email…"
            className="min-w-[220px] flex-1"
          />
          <SortSelect value={sort} onChange={handleSort} options={PREQUAL_SORT_OPTIONS} />
        </div>
      </div>

      {actionError && (
        <p className="m-0 text-small text-status-error" role="alert">
          {actionError}
        </p>
      )}

      {listError ? (
        <p className="m-0 text-small text-status-error" role="alert">
          {listError}
        </p>
      ) : !invites ? (
        <SkeletonList rows={2} data-testid="prequal-invites-loading" />
      ) : rows.length === 0 ? (
        filtered ? (
          <EmptyState
            icon={SearchX}
            title="No invitations match these filters"
            description="Try a different status, or clear the search box to see everything that's been sent from this site."
          />
        ) : (
          <EmptyState
            icon={Send}
            title={mineOnly ? "You haven't sent any invitations yet" : "No invitations sent yet"}
            description="Start a new prequalification and they'll get a link straight to your site — you'll be able to follow it from here."
          />
        )
      ) : (
        <>
          <div className="flex flex-col gap-space-3">
            {rows.map((invite) => (
              <InviteRow
                key={invite.id}
                invite={invite}
                showSender={!mineOnly}
                copied={copiedId === invite.id}
                busy={resend.pending || revoke.pending}
                onCopy={() => void copyLink(invite)}
                onResend={() => void resend.submit(invite.id)}
                onRevoke={() => void revoke.submit(invite.id)}
              />
            ))}
          </div>
          {/* The SERVER's echoed page and size, not the local state: this
              footer captions the rows actually on screen, and mid-flight those
              are still the previous page's. */}
          <PrequalPager
            page={invites.page}
            pageSize={invites.pageSize}
            totalCount={invites.totalCount}
            onPage={setPage}
          />
        </>
      )}
    </div>
  );
}

function InviteRow({
  invite,
  showSender,
  copied,
  busy,
  onCopy,
  onResend,
  onRevoke,
}: {
  invite: PrequalInvite;
  showSender: boolean;
  copied: boolean;
  busy: boolean;
  onCopy: () => void;
  onResend: () => void;
  onRevoke: () => void;
}) {
  const terminal = isTerminalInviteStatus(invite.status);
  const terminalReason = terminalInviteReason(invite.status);
  // "Live" here is the same set the stall reminder chases: still awaiting the
  // recipient, so the expiry is still a date worth showing.
  const live = isOutstandingInviteStatus(invite.status);

  return (
    <ListCard
      data-testid={`prequal-invite-row-${invite.id}`}
      title={`${invite.firstName} ${invite.lastName}`}
      status={
        <StatusBadge tone={PREQUAL_INVITE_STATUS_TONE[invite.status]}>{invite.status}</StatusBadge>
      }
      meta={
        <span className="flex flex-wrap items-center gap-x-space-2 gap-y-space-1">
          <span>{invite.role}</span>
          <span aria-hidden>·</span>
          <span>{invite.email}</span>
          <span aria-hidden>·</span>
          <span>
            Sent <Timestamp value={invite.sentAtUtc} />
          </span>
          {invite.sendCount > 1 && (
            <>
              <span aria-hidden>·</span>
              <span>Sent {invite.sendCount} times</span>
            </>
          )}
          {live && (
            <>
              <span aria-hidden>·</span>
              <span>
                Expires <Timestamp value={invite.expiresAtUtc} absolute />
              </span>
            </>
          )}
          {showSender && invite.invitedByLabel && (
            <>
              <span aria-hidden>·</span>
              <span>by {invite.invitedByLabel}</span>
            </>
          )}
        </span>
      }
      actions={
        <div className="flex flex-wrap items-center gap-space-2">
          <Button
            type="button"
            variant="text"
            size="sm"
            onClick={onCopy}
            data-testid={`prequal-invite-copy-${invite.id}`}
          >
            {copied ? "Link copied" : "Copy link"}
          </Button>
          {/* Disabled, not removed — a terminal invite is exactly when a
              partner goes looking for these buttons, and a control that has
              silently vanished reads as a bug rather than as an answer. The
              reason rides the native `title`. A shared Tooltip primitive now
              exists at design-system/components/ui/Tooltip.tsx and swapping to
              it is a deliberate follow-up: it changes the hover and focus
              behaviour of every disabled control on this row, which is a
              change worth making on its own rather than inside a list rebuild. */}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            title={terminalReason ?? undefined}
            disabled={busy || terminal}
            onClick={onResend}
            data-testid={`prequal-invite-resend-${invite.id}`}
          >
            Resend
          </Button>
          <Button
            type="button"
            variant="text"
            size="sm"
            title={terminalReason ?? undefined}
            disabled={busy || terminal}
            onClick={onRevoke}
            data-testid={`prequal-invite-revoke-${invite.id}`}
          >
            Revoke
          </Button>
        </div>
      }
    />
  );
}
